import React, { useState, useEffect } from 'react';
import StateSelect from "./states";
import BranchSelect from "./branchSelect";
import PartnershipField from "./partnership";
import LeadSelect from "./leadSelect";

const FormComponent = ({ setPage, campaign_details, campaign_name, show_how_did_you_hear, partnership }) => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        branch: '',
        benefitStatus: '',
        payGrade: '',
        birthdate: '',
        claimType: '',
        state: '',
        race: '',
        honeypot: '',
        lead: campaign_name !== '' ? campaign_name : '',
        
    });
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [loader, setLoader] = useState(false);
    const [phoneError, setPhoneError] = useState(''); // State for phone validation error

    // Handle form changes
    const handleChange = (e, name) => {
        const { value } = e.target;
        let formattedValue = value;

        // Format phone number if the field is 'phone'
        if (name === 'phone') {
            formattedValue = formatPhoneNumber(value);
            setPhoneError(validatePhoneNumber(formattedValue)); // Validate phone number
        }

        setFormData({
            ...formData,
            [name]: formattedValue,
        });
        console.log(formData)
    };

    // Format phone number to match US format (XXX-XXX-XXXX)
    const formatPhoneNumber = (phone) => {
        // Remove all non-digit characters
        const cleaned = phone.replace(/\D/g, '');

        // Apply formatting
        if (cleaned.length > 3 && cleaned.length <= 6) {
            return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
        } else if (cleaned.length > 6) {
            return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        }

        return cleaned;
    };

    // Validate phone number to ensure it has exactly 10 digits
    const validatePhoneNumber = (phone) => {
        const cleaned = phone.replace(/\D/g, ''); // Remove non-digits
        if (cleaned.length !== 10 && cleaned.length > 0) {
            return 'Phone number must be 10 digits';
        }
        return '';
    };

    // Handle the terms checkbox
    const handleTermsChange = (e) => {
        setAcceptTerms(e.target.checked);
    };

    // Update claimType based on benefitStatus
    useEffect(() => {
        let claimType = '';
        if (formData.benefitStatus === 'Never Filed') {
            claimType = 'New Claim Never Filed';
        } else if (formData.benefitStatus === 'Not Service Connected') {
            claimType = 'New Claim Previously Filed';
        } else if (formData.benefitStatus.match(/^\d+%$/)) {
            claimType = 'Increased Claim';
        }
        setFormData((prevData) => ({
            ...prevData,
            claimType: claimType,
        }));
    }, [formData.benefitStatus]);

    // Check if the form is valid
    useEffect(() => {
        const requiredFields = [
            formData.first_name,
            formData.last_name,
            formData.email,
            formData.phone,
            formData.state,
        ];
        const allFieldsFilled = requiredFields.every((field) => field.trim() !== '');
        const isPhoneValid = formData.phone.replace(/\D/g, '').length === 10; // Ensure phone has 10 digits
        setIsValid(allFieldsFilled && acceptTerms && isPhoneValid);
    }, [formData, acceptTerms]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoader(true);
        if (formData.honeypot !== '') {
            return;
        }

        const formUrl = 'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=00DHo000002gOAW';
        const orgId = '00DHo000002gOAW';
        const redirectUrl = 'https://vbg.com/thank-you';
        const birthdateFormatted = new Date(formData.birthdate).toLocaleDateString('en-US');

        const bodyData = new URLSearchParams({
            oid: orgId,
            retURL: redirectUrl,
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            phone: formData.phone,
            '00NPY000002aYem': formData.lead,
            '00NHo00000Y1GDX': formData.benefitStatus,
            '00NHo00000XtMmi': formData.branch,
            '00NHo00000Y1GDd': formData.state,
            '00NHo00000Y1iWU': formData.claimType,
            '00NHo00000Y1WSw': formData.race,
        });

        try {
            const response = await fetch(formUrl, {
                method: 'POST',
                mode: 'no-cors',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: bodyData.toString(),
            });

            if (response.ok) {
                setPage('success');
            } else {
                console.log('Opaque submission:', response);
                setLoader(false);
                setPage('success');
            }
        } catch (error) {
            console.error('Form submission error:', error);
        }
    };

    return (
        <form className="w-full max-w-lg mx-auto bg-none p-6" onSubmit={handleSubmit}>
            {loader && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
                </div>
            )}

            {/* Honeypot field - hidden */}
            <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={(e) => handleChange(e, 'honeypot')}
                className="hidden"
            />

            {/* Salesforce hidden fields */}
            <input type="hidden" name='captcha_settings' value='{"keyname":"reCAPTCHA","fallback":"true","orgId":"00DHo000002gOAW","ts":""}'/>
            <input type="hidden" name="oid" value="00DHo000002gOAW" />
            <input type="hidden" name="retURL" value="https://vbg.com/thank-you" />
            <input type="hidden" name="00NHo00000Y1iWU" value={formData.claimType} />
            <input type="hidden" name="00NPY000002aYem" value={formData.lead} title="Name of Campaign"/>

            <div className="grid grid-cols-2 gap-x-5">
                {/* First Name */}
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="first_name">
                        First Name*
                    </label>
                    <input
                        id="first_name"
                        name="first_name"
                        type="text"
                        size="20"
                        maxLength="40"
                        value={formData.first_name}
                        onChange={(e) => handleChange(e, 'first_name')}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        required
                    />
                </div>

                {/* Last Name */}
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="last_name">
                        Last Name*
                    </label>
                    <input
                        id="last_name"
                        name="last_name"
                        type="text"
                        size="20"
                        maxLength="80"
                        value={formData.last_name}
                        onChange={(e) => handleChange(e, 'last_name')}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        required
                    />
                </div>

                {/* Email */}
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                        Email*
                    </label>
                    <input
                        id="email"
                        name="email"
                        type="email"
                        size="20"
                        maxLength="80"
                        value={formData.email}
                        onChange={(e) => handleChange(e, 'email')}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        required
                    />
                </div>

                {/* Phone */}
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phone">
                        Phone*
                    </label>
                    <input
                        id="phone"
                        name="phone"
                        type="tel"
                        size="20"
                        maxLength="12" // Adjusted for hyphens
                        placeholder="999-999-9999"
                        value={formData.phone}
                        onChange={(e) => handleChange(e, 'phone')}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        required
                    />
                    {phoneError && (
                        <p className="text-red-500 text-xs italic">{phoneError}</p>
                    )}
                </div>

                {/* Branch Select */}
                <BranchSelect branch={formData.branch} handleChange={(e) => handleChange(e, 'branch')} />

                {/* Benefit Status */}
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="benefit_status">
                        What is your disability benefits status?*
                    </label>
                    <select
                        id="00NHo00000Y1GDX"
                        name="00NHo00000Y1GDX"
                        value={formData.benefitStatus}
                        onChange={(e) => handleChange(e, 'benefitStatus')}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        required
                        title="Current Rating"
                    >
                        <option value="">--None--</option>
                        <option value="Never Filed">Never Filed</option>
                        <option value="Not Service Connected">Not Service Connected</option>
                        <option value="0%">0%</option>
                        <option value="10%">10%</option>
                        <option value="20%">20%</option>
                        <option value="30%">30%</option>
                        <option value="40%">40%</option>
                        <option value="50%">50%</option>
                        <option value="60%">60%</option>
                        <option value="70%">70%</option>
                        <option value="80%">80%</option>
                        <option value="90%">90%</option>
                        <option value="100%">100%</option>
                    </select>
                </div>

                <StateSelect state={formData.state} handleChange={(e) => handleChange(e, 'state')} />

                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="race">
                        Race
                    </label>
                    <select
                        id="00NHo00000Y1WSw"
                        name="00NHo00000Y1WSw"
                        value={formData.race}
                        onChange={(e) => handleChange(e, 'race')}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        title="Race"
                    >
                        <option value="">--None--</option>
                        <option value="American Indian or Alaska Native">American Indian or Alaska Native</option>
                        <option value="Asian">Asian</option>
                        <option value="Black or African American">Black or African American</option>
                        <option value="Native Hawaiian or Other Pacific Islander">Native Hawaiian or Other Pacific Islander</option>
                        <option value="White">White</option>
                        <option value="Other Race">Other Race</option>
                    </select>
                </div>
            </div>

            {/* Terms & Conditions */}
            <div className="flex items-center justify-items-center mb-4">
                <input
                    id="terms"
                    name="terms"
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={handleTermsChange}
                    className="mr-2"
                    required
                />
                <label htmlFor="terms" className="text-gray-700">
                    Yes, I accept the Terms & Conditions of this site.
                </label>
            </div>
            <div className={'grid grid-cols-1 items-center justify-items-center mb-4'}>
                <div className="g-recaptcha" data-sitekey="6LdQQscqAAAAAONPgNMl4dBgMt4DxAY_fWi6KTpT"/>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-center">
                <button
                    className={`font-bold py-2 px-10 rounded focus:outline-none focus:shadow-outline ${
                        isValid ? 'bg-blue-500 hover:bg-blue-700 text-white' : 'bg-gray-400 text-white cursor-not-allowed'
                    }`}
                    type="submit"
                    disabled={!isValid}
                >
                    Submit
                </button>
            </div>
        </form>
    );
};

export default FormComponent;